import { useEffect, useRef } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { trackPushPermissionStatus } from "../services/engagementService";
import { normalizeEventFamilyId } from "../services/eventFamilyContext";
import { resolveNotificationNavigation } from "../utils/notificationNavigation";
import {
  getNativePushPermissionStatus,
  registerNativePush,
  requestNativePushPermission,
  saveNativePushToken,
} from "../services/nativeNotificationService";
import {
  getMyNotificationSettings,
  saveMyNotificationSettings,
  disableAllNotificationSettings,
  enableAllNotificationSettings,
} from "../services/notificationSettingsService";
import {
  emitPushPermissionChanged,
  isPushEnablePending,
  setPushEnablePending,
} from "../services/pushPermissionService";

const nativePushRegistrationState = {
  inFlight: new Set(),
  completed: new Set(),
};

const pendingNotificationTaps = new Map();
const processedNotificationTaps = new Map();
const processingNotificationTaps = new Set();
const PROCESSED_TAP_TTL_MS = 15000;
const MAX_NAVIGATION_RETRIES = 2;
const AndroidAileCall = registerPlugin("AileCall");

const flattenNotificationData = (input = {}) => {
  let nested = input?.data;
  if (typeof nested === "string") {
    try { nested = JSON.parse(nested); }
    catch { nested = null; }
  }
  return {
    ...(nested && typeof nested === "object" ? nested : {}),
    ...(input || {}),
  };
};

const getAndroidNativeBridge = () => {
  try { return Capacitor.getPlatform() === "android" ? AndroidAileCall : null; }
  catch { return null; }
};

export const notificationEventId = (data = {}) => String(
  data["google.message_id"] || data["gcm.message_id"] || data.messageId
  || data.notificationId || data.eventId
  || data.sourceId || data.kaynakId
  || `${data.type || data.module || "notification"}:${normalizeEventFamilyId(data) || "current"}:${data.url || data.pushPage || "home"}`
).trim();


// ozelTakvim sayfasına yönlendirildiğinde HealthPage'in internal ekranını açar.
// HealthPage mount olduğunda window.__healthOpenOzelTakvim'i tanımlar.
function triggerOzelTakvimIfNeeded(page) {
  if (page !== "ozelTakvim") return;
  // HealthPage henüz mount olmamış olabilir - kısa polling ile dene
  let attempts = 0;
  const interval = setInterval(() => {
    attempts += 1;
    if (typeof window.__healthOpenOzelTakvim === "function") {
      clearInterval(interval);
      window.__healthOpenOzelTakvim();
    } else if (attempts >= 20) {
      clearInterval(interval);
    }
  }, 150);
}

// AppDelegate'ten gelen push tap verisi { url, type, roomId, sourceId }
// ya da Capacitor event'inden gelen notification objesini parse eder.
export function extractNavigation(data) {
  data = flattenNotificationData(data);
  const { page, roomId, sourceId, action, occurrenceId } = resolveNotificationNavigation(data);

  return {
    page,
    roomId,
    sourceId,
    ...(action ? { action } : {}),
    ...(occurrenceId ? { occurrenceId } : {}),
    familyId: normalizeEventFamilyId(data),
    eventId: notificationEventId(data),
  };
}

/**
 * Native iOS push token'ını FCM'den alıp backend push_devices tablosuna kaydeder.
 * Ayrıca bildirime tıklanınca ilgili sayfaya yönlendirir.
 *
 * MİMARİ:
 * Capacitor + Firebase Messaging çakışması (AppDelegate UNUserNotificationCenterDelegate)
 * nedeniyle JS listener'lar güvenilir çalışmıyor. Çözüm:
 *
 * 1. AppDelegate.swift didReceive metodunda FCM data payload'u okunup
 *    window.aileHandlePushTap() global JS fonksiyonu evaluateJavaScript ile çağrılıyor.
 * 2. Bu hook aile aileHandlePushTap'i window'a kaydediyor ve navigasyonu yönetiyor.
 */

export function useNativePush({
  user,
  familyId,
  lang,
  pushUrlYonlendir,
  bildirimGoster,
  switchFamily,
  navigationReady = true,
}) {

  const pushUrlYonlandirRef = useRef(pushUrlYonlendir);
  useEffect(() => { pushUrlYonlandirRef.current = pushUrlYonlendir; });

  const bildirimGosterRef = useRef(bildirimGoster);
  useEffect(() => { bildirimGosterRef.current = bildirimGoster; });

  const userUidRef = useRef(user?.firebase_uid || user?.uid);
  useEffect(() => { userUidRef.current = user?.firebase_uid || user?.uid; }, [user?.uid, user?.firebase_uid]); // eslint-disable-line

  const familyIdRef = useRef(familyId);
  useEffect(() => { familyIdRef.current = familyId; }, [familyId]);

  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const langRef = useRef(lang);
  useEffect(() => { langRef.current = lang; }, [lang]);

  const switchFamilyRef = useRef(switchFamily);
  useEffect(() => { switchFamilyRef.current = switchFamily; }, [switchFamily]);

  const navigationReadyRef = useRef(navigationReady);
  useEffect(() => { navigationReadyRef.current = navigationReady; }, [navigationReady]);

  const navigationRetryStateRef = useRef(new Map());
  useEffect(() => () => {
    navigationRetryStateRef.current.forEach(({ timer }) => {
      if (timer) window.clearTimeout(timer);
    });
    navigationRetryStateRef.current.clear();
  }, []);

  // Sayfa + roomId/sourceId bilgisini pushUrlYonlendir formatına çevirip çağırır.
  // ozelTakvim için saglik sayfası açılır, ardından HealthPage internal ekranı tetiklenir.
  async function navigateFromNotification(event) {
    const { page, roomId, sourceId, action, occurrenceId, familyId: targetFamilyId, eventId } = event || {};
    if (!page) return;
    // ozelTakvim → saglik sayfasına git, sonra internal ekranı aç
    const navPage = page === "ozelTakvim" ? "saglik" : page;
    let url = `/?pushPage=${navPage}`;
    if (roomId) url += `&roomId=${encodeURIComponent(roomId)}`;
    if (sourceId) url += `&sourceId=${encodeURIComponent(sourceId)}`;
    if (action) url += `&action=${encodeURIComponent(action)}`;
    if (occurrenceId) url += `&occurrenceId=${encodeURIComponent(occurrenceId)}`;

    const id = eventId || notificationEventId(event);
    const processedAt = processedNotificationTaps.get(id);
    if ((processedAt && Date.now() - processedAt < PROCESSED_TAP_TTL_MS)
        || processingNotificationTaps.has(id)) return;
    if (navigationRetryStateRef.current.get(id)?.timer) return;
    if (processedAt) processedNotificationTaps.delete(id);
    pendingNotificationTaps.set(id, event);
    if (!navigationReadyRef.current
        || !pushUrlYonlandirRef.current
        || !userUidRef.current
        || !switchFamilyRef.current) {
      return;
    }

    processingNotificationTaps.add(id);
    try {
      let navigationFamilyId = familyIdRef.current;
      if (targetFamilyId && String(targetFamilyId) !== String(familyIdRef.current || "")) {
        const snapshot = await switchFamilyRef.current(targetFamilyId, { reason: "notification" });
        navigationFamilyId = snapshot.familyId;
        familyIdRef.current = snapshot.familyId;
      }
      processedNotificationTaps.set(id, Date.now());
      if (processedNotificationTaps.size > 200) {
        processedNotificationTaps.delete(processedNotificationTaps.keys().next().value);
      }
      pendingNotificationTaps.delete(id);
      const retryState = navigationRetryStateRef.current.get(id);
      if (retryState?.timer) window.clearTimeout(retryState.timer);
      navigationRetryStateRef.current.delete(id);
      pushUrlYonlandirRef.current(url, navigationFamilyId, userUidRef.current);
      if (Capacitor.getPlatform() === "android") {
        const bridge = getAndroidNativeBridge();
        bridge?.bekleyenBildirimiTuket?.({ eventId: id }).catch(() => {});
      } else if (Capacitor.getPlatform() === "ios") {
        try {
          window.webkit?.messageHandlers?.aileNativeBridge?.postMessage({
            action: "ackPendingNotification",
            eventId: id,
          });
        } catch {}
      }
      // HealthPage mount olduktan sonra ozelTakvim ekranını aç
      if (page === "ozelTakvim") {
        triggerOzelTakvimIfNeeded("ozelTakvim");
      }
    } catch (error) {
      const currentRetry = navigationRetryStateRef.current.get(id) || { attempt: 0, timer: null };
      if (!currentRetry.timer && currentRetry.attempt < MAX_NAVIGATION_RETRIES) {
        const nextAttempt = currentRetry.attempt + 1;
        const delayMs = currentRetry.attempt === 0 ? 350 : 1000;
        const timer = window.setTimeout(() => {
          navigationRetryStateRef.current.set(id, { attempt: nextAttempt, timer: null });
          navigateFromNotification(event).catch(() => {});
        }, delayMs);
        navigationRetryStateRef.current.set(id, { attempt: nextAttempt, timer });
      } else {
        console.warn("[nativePushTap] aile geçişi veya yönlendirme tamamlanamadı");
      }
    } finally {
      processingNotificationTaps.delete(id);
    }
  }

  // Native tap olayı uygulama arka plandayken JS listener'a her cihazda garanti
  // teslim edilmeyebilir. iOS/Android tap anında payload'u kalıcı native kuyruğa
  // da yazar; uygulama her öne geldiğinde bu kuyruğu tekrar istemek kayıp tap'i
  // tamamlar. notificationEventId tekilleştirmesi çift teslimi güvenle bastırır.
  async function replayPendingNativeNotification() {
    const platform = Capacitor.getPlatform();
    if (platform === "ios") {
      try {
        window.webkit?.messageHandlers?.aileNativeBridge?.postMessage({
          action: "consumePendingNotification",
        });
      } catch {}
      return;
    }

    if (platform === "android") {
      const nativeBridge = getAndroidNativeBridge();
      if (!nativeBridge?.bekleyenBildirimiAl) return;
      const pending = await nativeBridge.bekleyenBildirimiAl();
      const nav = extractNavigation(pending || {});
      if (nav.page) await navigateFromNotification(nav);
    }
  }

  useEffect(() => {
    if (!navigationReadyRef.current
        || !userUidRef.current
        || !switchFamilyRef.current
        || !pushUrlYonlandirRef.current) return;
    [...pendingNotificationTaps.values()].forEach((event) => {
      navigateFromNotification(event).catch(() => {});
    });
  }, [user?.uid, user?.firebase_uid, familyId, switchFamily, navigationReady]); // eslint-disable-line

  // ── window.aileHandlePushTap — AppDelegate'ten çağrılan global fonksiyon ──────────────
  // AppDelegate.swift'in didReceive metodu evaluateJavaScript ile bu fonksiyonu çağırır.
  // Capacitor plugin listener'larına güvenemediğimiz için bu yol kullanılıyor (SADECE iOS).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (Capacitor.getPlatform() !== "ios") return;

    window.aileHandlePushTap = (data) => {
      const nav = extractNavigation(data || {});
      if (nav.page) {
        navigateFromNotification(nav).catch(() => {});
      } else {
        console.warn("[nativePushTap] hedef sayfa bulunamadı, data:", JSON.stringify(data));
      }
    };

    window.aileHandlePushReceived = (data) => {
      const payload = flattenNotificationData(data);
      const nav = extractNavigation(payload);
      const baslik = payload.title || payload.baslik || "";
      const aciklama = payload.body || payload.aciklama || "";
      if ((!baslik && !aciklama) || !bildirimGosterRef.current) return;
      bildirimGosterRef.current(baslik, aciklama, payload.type || nav.page || "default", {
        url: payload.url || (nav.page ? `/?pushPage=${nav.page}` : ""),
        roomId: nav.roomId,
        sourceId: nav.sourceId,
        familyId: nav.familyId,
        eventId: nav.eventId,
        data: payload,
      });
    };

    const nativeQueuedTaps = Array.isArray(window.__ailePendingPushTaps)
      ? window.__ailePendingPushTaps.splice(0)
      : [];
    nativeQueuedTaps.forEach((data) => {
      const nav = extractNavigation(data || {});
      if (nav.page) navigateFromNotification(nav).catch(() => {});
    });

    replayPendingNativeNotification().catch(() => {});

    return () => {
      window.aileHandlePushTap = null;
      window.aileHandlePushReceived = null;
    };
  }, []); // eslint-disable-line

  // ── Firebase Messaging push işleme ───────────────────────────────────────
  // notificationActionPerformed hem iOS hem Android'de uygulama arka plandayken
  // yapılan bildirim dokunuşunun canlı teslim yoludur. Kalıcı native kuyruk ise
  // cold start ve listener henüz hazır değilken güvenlik ağı olarak kalır.
  // Android'e özel işlemler:
  //   1. createChannel → HIGH importance bildirim kanalı (heads-up banner + ses/titreşim).
  //      Backend push'ları bu channel_id ("aile_yonetimi_default") ile gönderir; böylece
  //      bildirim panelinde kalmak yerine ekranın üstünde açılır banner olarak görünür.
  //   2. notificationReceived → uygulama ÖN PLANDAYKEN tetiklenir. Sistem, ön plandaki
  //      notification-tipi mesajı otomatik banner göstermez; bu yüzden uygulama içi
  //      toast (bildirimGoster) ile gösteriyoruz. Android Lite'ta kısıtlı bildirimler
  //      zaten backend'de engellendiği için buraya ulaşmaz; ekstra güvenlik için yine de
  //      isAndroidRestrictedNotification kontrolü uygulama tarafında da yapılır (App.js).
  //   3. notificationActionPerformed → kullanıcı bildirime DOKUNDUĞUNDA (arka plan/kapalı)
  //      tetiklenir. Doğru sayfaya yönlendirir.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const platform = Capacitor.getPlatform();
    if (platform !== "android" && platform !== "ios") return;

    let receivedHandle = null;
    let actionHandle = null;
    let cancelled = false;

    (async () => {
      try {
        const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");

        if (platform === "android") {
          // 1) HIGH importance kanalı oluştur (varsa Android idempotent davranır).
          try {
            // ÖNEMLİ: Android'de bir bildirim kanalı bir kez oluşturulduktan
            // sonra ÖNEM DÜZEYİ PROGRAMLA YÜKSELTİLEMEZ. Cihazda daha önce
            // düşük önemli varsayılan kanal oluştuğu için heads-up banner
            // çıkmıyordu. Bu yüzden kanal kimliği "_v2" olarak yenilendi.
            // AndroidManifest.xml'deki default_notification_channel_id meta-data
            // değeri de bu kimlikle BİREBİR aynı olmalıdır.
            await FirebaseMessaging.createChannel({
              id: "aile_yonetimi_v2",
              name: "Genel Bildirimler",
              description: "Aile Yönetimi bildirimleri",
              importance: 5, // IMPORTANCE_HIGH → heads-up banner
              visibility: 1, // VISIBILITY_PUBLIC
              sound: "default",
              vibration: true,
              lights: true,
            });
            console.log("[androidPush] Bildirim kanalı hazır: aile_yonetimi_v2");
          } catch (channelErr) {
            console.warn("[androidPush] Kanal oluşturulamadı:", channelErr?.message || channelErr);
          }
        }

        if (cancelled) return;

        // 2) Ön planda bildirim → uygulama içi toast
        if (platform === "android") {
          receivedHandle = await FirebaseMessaging.addListener("notificationReceived", (event) => {
            try {
              const n = event?.notification || {};
              const data = n.data || {};
              const nav = extractNavigation({ ...data, messageId: n.id, url: data.url });
              const baslik = n.title || data.title || "";
              const aciklama = n.body || data.body || "";
              if (!baslik && !aciklama) return;

              // Yönlendirme meta verisini toast'a iliştir; tıklanınca App.js
              // onNotificationClick akışı doğru sayfaya götürür.
              const type = data.type || data.tip || (nav.page ? nav.page : "default");
              if (bildirimGosterRef.current) {
                bildirimGosterRef.current(baslik, aciklama, type, {
                  url: data.url || (nav.page ? `/?pushPage=${nav.page}` : ""),
                  roomId: nav.roomId || data.roomId || null,
                  sourceId: nav.sourceId || data.sourceId || null,
                  familyId: nav.familyId,
                  eventId: nav.eventId,
                  data,
                });
              }
            } catch (e) {
              console.warn("[androidPush] notificationReceived işleme hatası:", e?.message || e);
            }
          });
        }

        // 3) Bildirime dokunma → sayfaya yönlendir
        actionHandle = await FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
          try {
            const n = event?.notification || {};
            const data = n.data || {};
            const nav = extractNavigation({ ...data, messageId: n.id, url: data.url });
            if (nav.page) {
              navigateFromNotification(nav).catch(() => {});
            } else {
              console.warn(`[${platform}Push] bildirim hedefi bulunamadı`);
            }
          } catch (e) {
            console.warn(`[${platform}Push] notificationActionPerformed işleme hatası:`, e?.message || e);
          }
        });

        await replayPendingNativeNotification();

      } catch (err) {
        console.warn(`[${platform}Push] Kurulum hatası:`, err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
      if (receivedHandle && receivedHandle.remove) receivedHandle.remove();
      if (actionHandle && actionHandle.remove) actionHandle.remove();
    };
  }, []); // eslint-disable-line

  // ── FCM token kaydı ────────────────────────────────────────────────────
  useEffect(() => {
    const uid = user?.firebase_uid || user?.uid;
    if (!uid || !familyId) return;
    if (!Capacitor.isNativePlatform()) return;

    const normalizedLang = lang === "en" ? "en" : "tr";
    const registrationKey = `${uid}:${familyId}:${normalizedLang}`;

    if (
      nativePushRegistrationState.inFlight.has(registrationKey) ||
      nativePushRegistrationState.completed.has(registrationKey)
    ) {
      return;
    }

    let cancelled = false;
    nativePushRegistrationState.inFlight.add(registrationKey);

    (async () => {
      try {
        let perm = await getNativePushPermissionStatus();
        trackPushPermissionStatus({ familyId, status: perm, language: normalizedLang, metadata: { source: "checkPermissions" } });

        if (perm === "prompt") {
          perm = await requestNativePushPermission();
          trackPushPermissionStatus({ familyId, status: perm, language: normalizedLang, metadata: { source: "requestPermissions" } });
        }
        emitPushPermissionChanged(perm);

        if (cancelled) return;
        if (perm !== "granted") {
          const settings = await getMyNotificationSettings(familyId, uid);
          if (settings.pushEnabled !== false || settings.nativeEnabled !== false) {
            await saveMyNotificationSettings(familyId, uid, disableAllNotificationSettings(settings));
            emitPushPermissionChanged(perm);
          }
          return;
        }

        const token = await registerNativePush({ user, familyId, lang: normalizedLang, silent: true });
        if (!cancelled && token) {
          nativePushRegistrationState.completed.add(registrationKey);
        }
      } catch (e) {
        console.warn("[nativeNotif] FCM token kaydedilemedi:", e?.message || e);
      } finally {
        nativePushRegistrationState.inFlight.delete(registrationKey);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.uid, user?.firebase_uid, familyId, lang]); // eslint-disable-line

  // iOS token'ı izin verildiği anda henüz hazır olmayabilir. Firebase'in daha
  // sonra yayınladığı tokenReceived olayı aynı idempotent backend kaydını tamamlar.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let tokenHandle = null;
    let appHandle = null;
    let cancelled = false;
    const pendingReplayTimers = new Set();

    (async () => {
      const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
      tokenHandle = await FirebaseMessaging.addListener("tokenReceived", async ({ token }) => {
        if (cancelled || !token || !userRef.current || !familyIdRef.current) return;
        if (await getNativePushPermissionStatus() !== "granted") return;
        const activeUser = userRef.current;
        const activeFamilyId = familyIdRef.current;
        const activeLang = langRef.current === "en" ? "en" : "tr";
        await saveNativePushToken({
          user: activeUser,
          familyId: activeFamilyId,
          token,
          lang: activeLang,
        }).then(() => {
          const uid = activeUser.firebase_uid || activeUser.uid;
          nativePushRegistrationState.completed.add(`${uid}:${activeFamilyId}:${activeLang}`);
        }).catch(() => {});
      });

      appHandle = await CapacitorApp.addListener("appStateChange", async ({ isActive }) => {
        if (!isActive || cancelled) return;
        await replayPendingNativeNotification().catch(() => {});
        // iOS'ta UNUserNotificationCenter callback'i ile appStateChange sırası
        // cihaz/OS sürümüne göre değişebilir. Native kayıt birkaç yüz ms sonra
        // oluşursa da kaybolmaması için yalnız yerel köprüye iki sınırlı tekrar yap.
        [350, 1200].forEach((delayMs) => {
          const timer = window.setTimeout(() => {
            pendingReplayTimers.delete(timer);
            if (!cancelled) replayPendingNativeNotification().catch(() => {});
          }, delayMs);
          pendingReplayTimers.add(timer);
        });
        if (!userRef.current || !familyIdRef.current) return;
        const status = await getNativePushPermissionStatus();
        emitPushPermissionChanged(status);
        if (status !== "granted") return;

        const uid = userRef.current.firebase_uid || userRef.current.uid;
        const activeLang = langRef.current === "en" ? "en" : "tr";
        const registrationKey = `${uid}:${familyIdRef.current}:${activeLang}`;
        if (!nativePushRegistrationState.completed.has(registrationKey)) {
          const token = await registerNativePush({
            user: userRef.current,
            familyId: familyIdRef.current,
            lang: activeLang,
            silent: true,
          });
          if (token) nativePushRegistrationState.completed.add(registrationKey);
        }

        if (isPushEnablePending(uid)) {
          const settings = await getMyNotificationSettings(familyIdRef.current, uid);
          await saveMyNotificationSettings(
            familyIdRef.current,
            uid,
            enableAllNotificationSettings(settings)
          );
          setPushEnablePending(uid, false);
          emitPushPermissionChanged("granted");
        }
      });
    })().catch(() => {});

    return () => {
      cancelled = true;
      pendingReplayTimers.forEach((timer) => window.clearTimeout(timer));
      pendingReplayTimers.clear();
      tokenHandle?.remove?.();
      appHandle?.remove?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- native listener tek kez kurulur; güncel state ref'lerden okunur
}
